import React, { useState } from "react";
import {
  Grid,
  Box,
  Typography,
  IconButton,
  Paper,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { API_BASE_URL } from "../../utils/api";
import axios from "axios";

const FileGrid = ({ files, currentPath, onDelete, onRename }) => {
  const [editingFile, setEditingFile] = useState(null);
  const [newName, setNewName] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewType, setPreviewType] = useState("");

  const openMenu = (event, file) => {
    setAnchorEl(event.currentTarget);
    setSelectedFile(file);
  };

  const closeMenu = () => {
    setAnchorEl(null);
    setSelectedFile(null);
  };

  const startEditing = () => {
    setEditingFile(selectedFile);
    setNewName(selectedFile);
    closeMenu();
  };

  const cancelEditing = () => {
    setEditingFile(null);
    setNewName("");
  };

  const saveRename = () => {
    if (newName && newName !== editingFile) {
      onRename(editingFile, newName);
    }
    cancelEditing();
  };

  const handleDownload = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/download?path=${encodeURIComponent(`${currentPath}/${selectedFile}`)}`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", selectedFile);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Download failed", err);
    } finally {
      closeMenu();
    }
  };

  const handleDelete = () => {
    onDelete(selectedFile);
    closeMenu();
  };

  const handlePreview = () => {
    const ext = selectedFile?.split(".").pop().toLowerCase();
    const fileUrl = `${API_BASE_URL}/download?path=${encodeURIComponent(`${currentPath}/${selectedFile}`)}`;

    if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) {
      setPreviewType("image");
      setPreviewFile(fileUrl);
    } else if (ext === "pdf") {
      setPreviewType("pdf");
      setPreviewFile(fileUrl);
    } else {
      alert("Preview not supported for this file type.");
    }

    closeMenu();
  };

  return (
    <>
      <Grid container spacing={3} mt={2}>
        {files.map((file) => (
          <Grid item key={file} xs={12} sm={6} md={4} lg={3} xl={2}>
            <Paper
              elevation={3}
              sx={{
                p: 2,
                borderRadius: 3,
                position: "relative",
                transition: "0.3s",
                "&:hover": { boxShadow: 6, transform: "translateY(-2px)" },
              }}
            >
              {/* 3-Dot Menu Button */}
              <IconButton
                size="small"
                sx={{ position: "absolute", top: 8, right: 8 }}
                onClick={(e) => openMenu(e, file)}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>

              {/* File Icon */}
              <Box display="flex" justifyContent="center" mb={1}>
                <InsertDriveFileIcon sx={{ fontSize: 50, color: "#4caf50" }} />
              </Box>

              {/* Filename or Rename Field */}
              {editingFile === file ? (
                <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                  <TextField
                    size="small"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    sx={{ width: "70%" }}
                  />
                  <IconButton size="small" onClick={saveRename}>
                    ✔️
                  </IconButton>
                  <IconButton size="small" onClick={cancelEditing}>
                    ❌
                  </IconButton>
                </Box>
              ) : (
                <Typography
                  variant="body2"
                  align="center"
                  sx={{
                    maxWidth: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {file}
                </Typography>
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* File Options Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={handlePreview}>Preview</MenuItem>
        <MenuItem onClick={handleDownload}>Download</MenuItem>
        <MenuItem onClick={startEditing}>Rename</MenuItem>
        <MenuItem onClick={handleDelete}>Delete</MenuItem>
      </Menu>

      {/* Preview Modal */}
      <Dialog open={!!previewFile} onClose={() => setPreviewFile(null)} maxWidth="md" fullWidth>
        <DialogTitle>Preview: {selectedFile}</DialogTitle>
        <DialogContent dividers sx={{ minHeight: "500px" }}>
          {previewType === "image" && (
            <Box display="flex" justifyContent="center">
              <img
                src={previewFile}
                alt={selectedFile}
                style={{ maxWidth: "100%", maxHeight: "600px" }}
              />
            </Box>
          )}
          {previewType === "pdf" && (
            <iframe
              src={previewFile}
              title={selectedFile}
              width="100%"
              height="600px"
              style={{ border: "none" }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FileGrid;
